/**
 * How a step's page keeps its answers: saved as the guardian goes, a moment
 * after they stop, so a phone that locks halfway loses nothing. Nothing saves
 * on load: the first save is the guardian's first change. Submit saves
 * anything still waiting first, then submits; the answers can't change while
 * it does, and nothing saves after a submission is accepted. The server's
 * errors stay on the page with the answers, until the family changes one. An
 * accepted submit goes to the next step still open, or back to the overview.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSaveChecklistAnswers } from '../useSaveChecklistAnswers';
import { useSubmitChecklistStep } from '../useSubmitChecklistStep';

/** How long after the last change the answers save. */
export const SAVE_DELAY_MS = 800;

const COULDNT_SAVE = "We couldn't save your answers. Check your connection; they'll save again when you make a change or submit.";
const COULDNT_SUBMIT = "We couldn't submit your answers. Check your connection and try again.";

export interface StepAnswersOptions<T> {
  itemId: string;
  /** The answers the page opens with; read once. */
  initial: T;
  /** The year's enroll page, where a submit with nothing next goes. */
  overviewPath: string;
  stepPath: (itemId: string) => string;
  /** Answers the server doesn't have yet, which the first save or Submit sends even if nothing changes. */
  unsentAtStart?: boolean;
}

export function useStepAnswers<T>({ itemId, initial, overviewPath, stepPath, unsentAtStart = false }: StepAnswersOptions<T>) {
  const navigate = useNavigate();
  const [save] = useSaveChecklistAnswers();
  const [submit] = useSubmitChecklistStep();

  const [answers, setAnswers] = useState<T>(initial);
  const latest = useRef(answers);
  const unsaved = useRef(false);
  const unsent = useRef(unsentAtStart);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Saves run one after another, so the last one to land is the newest.
  const queue = useRef<Promise<boolean>>(Promise.resolve(true));
  // While a submission is on its way the answers hold still, and once it's
  // accepted nothing saves again: a save then would rewrite the done step.
  const locked = useRef(false);
  const accepted = useRef(false);

  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Saves whatever hasn't been saved; true once the server has it all.
  const flush = useCallback((): Promise<boolean> => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    queue.current = queue.current.then(async () => {
      if (accepted.current || !unsaved.current) return true;
      unsaved.current = false;
      unsent.current = false;
      setSaveState('saving');
      try {
        const result = await save({ variables: { input: { itemId, answers: latest.current } } });
        const payload = result.data?.saveChecklistAnswers;
        const errors = payload ? payload.errors : [COULDNT_SAVE];
        if (errors.length > 0) {
          unsaved.current = true;
          setSaveErrors(errors);
          setSaveState('idle');
          return false;
        }
        setSaveErrors([]);
        // A change made while this one was on its way isn't saved yet.
        setSaveState(unsaved.current ? 'idle' : 'saved');
        return true;
      } catch {
        unsaved.current = true;
        setSaveErrors([COULDNT_SAVE]);
        setSaveState('idle');
        return false;
      }
    });
    return queue.current;
  }, [itemId, save]);

  // Saves until nothing is left unsaved, or a save fails.
  const flushAll = useCallback(async (): Promise<boolean> => {
    do {
      if (!(await flush())) return false;
    } while (unsaved.current && !accepted.current);
    return true;
  }, [flush]);

  // Leaving the page saves what's still waiting.
  useEffect(() => () => {
    if (unsaved.current) void flush();
  }, [flush]);

  const change = (update: (current: T) => T) => {
    if (locked.current || accepted.current) return;
    const next = update(latest.current);
    latest.current = next;
    setAnswers(next);
    setSubmitErrors([]);
    unsaved.current = true;
    setSaveState('idle');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush(); }, SAVE_DELAY_MS);
  };

  const unlock = () => {
    locked.current = false;
    setSubmitting(false);
  };

  const handleSubmit = async () => {
    if (locked.current) return;
    locked.current = true;
    setSubmitting(true);
    setSubmitErrors([]);
    if (unsent.current) unsaved.current = true;
    if (!(await flushAll())) {
      unlock();
      return;
    }
    try {
      // The family's pages read the year's statuses and progress; they change here.
      const result = await submit({ variables: { input: { itemId } }, refetchQueries: ['MyFamily'] });
      const payload = result.data?.submitChecklistStep;
      const errors = payload ? payload.errors : [COULDNT_SUBMIT];
      if (errors.length > 0) {
        setSubmitErrors(errors);
        unlock();
        return;
      }
      accepted.current = true;
      unsaved.current = false;
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      navigate(payload?.nextItemId ? stepPath(payload.nextItemId) : overviewPath);
    } catch {
      setSubmitErrors([COULDNT_SUBMIT]);
      unlock();
    }
  };

  return { answers, change, submit: handleSubmit, saveState, errors: [...saveErrors, ...submitErrors], submitting };
}
